import { useDispatch, useSelector } from "react-redux";
import { distActions } from "../store";
import { fetchWrapper } from "../helpers";
import { message } from "antd";
import {useTranslation} from "react-i18next";

const baseUrl = `${process.env.REACT_APP_API_URL}`;

export const useModeratorPost = (employeeId) => {
    const dispatch = useDispatch();

    const { t } = useTranslation();
    const employee = useSelector((state) => state.dist.selected_employees.find((e) => e.id === employeeId));
    const moderatorPost = useSelector((state) => state.dist.moderatorPosts[employeeId] || {});
    const tempImages = useSelector((state) => state.dist.tempImages[employeeId] || []);
    const tempVideo = useSelector((state) => state.dist.tempVideos[employeeId]);
    const isGenerating = useSelector((state) => state.dist.generating);
    const formState = useSelector((state) => state.dist.form);
    const jobStatus = useSelector((state) => state.dist.jobStatus?.[employeeId]);

    // Initialize post from job result
    const initializePost = (jobResult) => {
        if (jobResult && !moderatorPost.initialized) {
            const parsed = jobResult || {};
            dispatch(
                distActions.setModeratorPost({
                    employeeId,
                    postData: {
                        post: parsed?.result?.post || "Write your own post, or generate it with the form. If you see this, please regenerate the post",
                        postTitle: parsed?.result?.postTitle || "New post",
                        hashtags: parsed?.result?.hashtags || "#NewPost",
                        media: parsed?.result?.media,
                        initialized: true,
                        published: false,
                        publishTime: new Date().toISOString(),
                        weekDay: "",
                        socialNetwork: [],
                        sent: false,
                    },
                })
            );
        }
    };

    // Update post content
    const updatePostContent = (updates) => {
        dispatch(
            distActions.setModeratorPost({
                employeeId,
                postData: updates,
            })
        );
    };

    // Handle image operations
    const addImages = (files) => {
        const newImages = Array.from(files);
        newImages.forEach((image) => {
            if (tempImages.length < 6) {
                dispatch(distActions.addTempImage({ employeeId, image }));
            }
        });
    };

    const removeImage = (index) => {
        dispatch(distActions.removeTempImage({ employeeId, index }));
    };

    const clearImages = () => {
        dispatch(distActions.clearTempImages({ employeeId }));
    };

    // Handle video operations
    const setVideo = (video) => {
        dispatch(distActions.setTempVideo({ employeeId, video }));
    };

    const clearVideo = () => {
        dispatch(distActions.clearTempVideo({ employeeId }));
    };

    const pollJob = async (jobId, { interval = 1500, max = 120, signal } = {}) => {
        let attempts = 0;
        while (attempts < max) {
            if (signal?.aborted) throw new Error("Aborted");
            const res = await fetchWrapper.get(`${baseUrl}/dist/job/${jobId}`);
            const backendState = res?.state;
            const normalized = backendState === "completed" ? "completed" : backendState === "failed" ? "failed" : backendState === "delayed" ? "delayed" : "queued"; // waiting/active → queued

            // keep Redux status fresh for UI overlays/icons
            dispatch(distActions.setJobStatus({ employeeId, jobId, status: normalized, result: res?.result, error: res?.error }));

            if (normalized === "completed") return res;
            if (normalized === "failed") {
                const e = new Error(res?.error || "Job failed");
                e.__jobFailed = true;
                throw e;
            }

            await new Promise((r) => setTimeout(r, interval));
            attempts++;
        }
        throw new Error("Polling timed out");
    };

    const regeneratePost = async (customPrompt = null) => {
        const ac = new AbortController();
        try {
            // 1) If previous run failed — clean slate
            if (jobStatus?.status === "failed" || jobStatus?.error) {
                dispatch(distActions.clearModeratorPost({ employeeId }));
                if (distActions.clearJobStatus) {
                    dispatch(distActions.clearJobStatus({ employeeId }));
                } else {
                    // fallback
                    dispatch(distActions.setJobStatus({ employeeId, jobId: null, status: null, result: null, error: null }));
                }
            }

            // 2) Start a new regen job
            const content = formState.contentType === 0 ? formState.postLink : formState.postText;
            const start = await fetchWrapper.post(`${baseUrl}/dist/regenerate`, {
                employeeId, // backend accepts employeeId or userId
                title: formState.title,
                article: content,
                instruction: customPrompt ?? formState.instruction ?? "",
                contentType: formState.contentType === 0 ? "link" : "text",
                languages: formState.languages,
                originalPost: moderatorPost.post
            });

            const jobId = start?.jobId;
            if (!jobId) throw new Error("No jobId returned");

            dispatch(distActions.setJobStatus({ employeeId, jobId, status: "queued", result: null, error: null }));

            // 3) Poll to completion
            const done = await pollJob(jobId, { signal: ac.signal });

            // 4) Replace content in Redux
            let payload = done?.result;
            // some processors return stringified JSON
            if (typeof payload === "string") {
                try {
                    payload = JSON.parse(payload);
                } catch (_) {}
            }
            const r = payload?.result ?? payload ?? {};

            dispatch(
                distActions.setModeratorPost({
                    employeeId,
                    postData: {
                        post: r?.post || "Write your own post, or generate it with the form. If you see this, please regenerate the post",
                        postTitle: r?.postTitle || "New post",
                        hashtags: r?.hashtags || "#NewPost",
                        media: r?.media || [],
                        initialized: true,
                        sent: false,
                        publishTime: moderatorPost?.publishTime || new Date().toISOString(), // keep existing schedule
                    },
                })
            );
            const regenerationSuccessMessage = t('postDistribution.regenerationSuccess')
            message.success(regenerationSuccessMessage);
        } catch (err) {
            dispatch(
                distActions.setJobStatus({
                    employeeId,
                    jobId: jobStatus?.jobId || null,
                    status: "failed",
                    result: null,
                    error: err?.message || "Regeneration failed",
                })
            );
            const regenerationFailureMessage = t('postDistribution.regenerationFailure')
            message.error(regenerationFailureMessage);
            console.error(err);
        } finally {
            dispatch(distActions.setGenerating(false));
        }

        return () => ac.abort();
    };

    // Get user social media accounts
    const connectedPlatforms = useSelector((state) => state.dist.employeeSocialMediaMap?.[employeeId] || []);

    // Submit final post (this is where API calls happen)
    const submitPost = async (autopublication = false) => {
        try {
            dispatch(distActions.setGenerating(true));

            // Prepare form data for submission
            const formData = new FormData();

            // Add post data
            formData.append("employeeId", employeeId);
            formData.append("post", moderatorPost.post || "");
            formData.append("postTitle", moderatorPost.postTitle);
            formData.append("hashtags", moderatorPost.hashtags || "");
            formData.append("media", JSON.stringify(moderatorPost.media || []));
            formData.append("publishTime", moderatorPost.publishTime || new Date().toISOString());
            formData.append("socialNetwork", Array.isArray(moderatorPost.socialNetwork) ? moderatorPost.socialNetwork.join(",") : moderatorPost.socialNetwork || "");
            formData.append("autopublication", autopublication);
            for (let [key, value] of formData.entries()) {
                console.log(`${key}:`, value);
            }

            // Add images
            tempImages.forEach((image, index) => {
                formData.append(`images`, image);
            });

            // Add video if exists
            if (tempVideo) {
                formData.append("video", tempVideo);
            }

            const apiUrl = `${process.env.REACT_APP_API_URL}/dist/send/`;
            const response = await fetchWrapper.post(apiUrl, formData, { raw: false });

            if (response?.success) {
                const submissionSuccessMessage = t('postDistribution.submissionSuccess')
                message.success(submissionSuccessMessage);

                // Clear the moderator post data after successful submission
                dispatch(
                    distActions.setModeratorPost({
                        employeeId,
                        postData: { sent: true }, // ✅ Устанавливаем флаг
                    })
                );

                return response;
            } else {
                const submissionErrorMessage = t('postDistribution.submissionFailure')
                throw new Error(submissionErrorMessage);
            }
        } catch (error) {
            const submissionErrorMessage = t('postDistribution.submissionFailure')
            message.error(submissionErrorMessage);
            console.error(error);
            throw error;
        } finally {
            dispatch(distActions.setGenerating(false));
        }
    };

    // Get current post data with fallbacks
    const getCurrentPost = () => ({
        post: moderatorPost.post || "Write your own post, or generate it using the form.",
        postTitle: moderatorPost.postTitle || "New post - please write it yourself or generate it.",
        hashtags: moderatorPost.hashtags || "#NewPost",
        media: moderatorPost.media || [],
        publishTime: moderatorPost.publishTime || new Date().toISOString(),
        weekDay: moderatorPost.weekDay || "",
        socialNetwork: moderatorPost.socialNetwork || [],
        sent: moderatorPost.sent || false,
        published: false,
    });

    // Validate post data
    const validatePost = () => {
        const errors = [];
        const currentPost = getCurrentPost();

        if (!currentPost.post || currentPost.post.trim().length < 25) {
            const errorMessage = t('postDistribution.characterMinLimitError');
            errors.push(errorMessage);
        }

        if (currentPost.post.length > 2000) {
            const errorMessage = t('postDistribution.characerMaxLimitError');
            errors.push(errorMessage);
        }

        if (!currentPost.hashtags || currentPost.hashtags.trim().length === 0) {
            const errorMessage = t('postDistribution.hashtagsRequired')
            errors.push(errorMessage);
        }

        // Check total file size
        const totalSize = tempImages.reduce((size, image) => size + image.size, 0) + (tempVideo ? tempVideo.size : 0);

        if (totalSize > 10000000) {
            // 10MB
            const errorMessage = t('postDistribution.fileSizeLimitExceeded')
            errors.push("Total media size must be less than 10MB");
        }

        return {
            isValid: errors.length === 0,
            errors,
        };
    };

    // Clear all data for this employee
    const clearPost = () => {
        dispatch(distActions.clearModeratorPost({ employeeId }));
    };

    const toggleSocialNetwork = (platform) => {
        const current = Array.isArray(moderatorPost.socialNetwork) ? moderatorPost.socialNetwork : [];
        const updated = current.includes(platform) ? current.filter((p) => p !== platform) : [...new Set([...current, platform])]; // гарантируем уникальность

        dispatch(
            distActions.setModeratorPost({
                employeeId,
                postData: { socialNetwork: updated },
            })
        );
    };

    return {
        // Data
        employee,
        moderatorPost,
        tempImages,
        tempVideo,
        isGenerating,
        currentPost: getCurrentPost(),

        // Actions
        initializePost,
        updatePostContent,
        addImages,
        removeImage,
        clearImages,
        setVideo,
        clearVideo,
        regeneratePost,
        submitPost,
        validatePost,
        clearPost,
        toggleSocialNetwork,
    };
};
